import { Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { BreadcrumbItem } from '@/contexts/breadcrumb-context'
import { SidebarTrigger, useSidebar } from '../ui/sidebar'
import { Button } from '../ui/button'
import { NotificationFeed } from '../notifications/notification-feed'
import { TaskFeed } from '../tasks/task-feed'
import {
  Breadcrumb,
  BreadcrumbItem as BreadcrumbItemUI,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'

interface ContentHeaderProps {
  breadcrumbs: BreadcrumbItem[]
}

/**
 * The top strip of the floating content card. On desktop it carries the
 * page's breadcrumb trail with a round back button to the nearest linked
 * ancestor, and disappears on pages without breadcrumbs. On mobile it is the
 * shell's only top bar, so it always renders the sidebar trigger plus the
 * task and notification feeds (their popovers can't live inside the sidebar
 * sheet, whose scroll lock would block scrolling their lists).
 */
export function ContentHeader({ breadcrumbs }: ContentHeaderProps) {
  const { isMobile } = useSidebar()

  if (!isMobile && breadcrumbs.length === 0) return null

  const parent = breadcrumbs.slice(0, -1).reverse().find((crumb) => crumb.href)

  return (
    <div className="flex h-12 shrink-0 items-center gap-2 px-4">
      {isMobile && <SidebarTrigger className="-ml-1 rounded-lg" />}
      {parent?.href && (
        <Button
          asChild
          variant="ghost"
          size="icon"
          className={cn('size-7 rounded-full bg-muted/60 hover:bg-muted', !isMobile && '-ml-1')}
        >
          <Link to={parent.href} aria-label={`Back to ${parent.label}`}>
            <ChevronLeft className="size-4" />
          </Link>
        </Button>
      )}
      <Breadcrumb className="min-w-0 flex-1">
        <BreadcrumbList>
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1

            return (
              <div key={index} className="contents">
                <BreadcrumbItemUI className={index === 0 ? 'hidden md:block' : ''}>
                  {isLast ? (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={crumb.href || '#'}>{crumb.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItemUI>
                {!isLast && <BreadcrumbSeparator className="hidden md:block" />}
              </div>
            )
          })}
        </BreadcrumbList>
      </Breadcrumb>
      {isMobile && (
        <div className="flex items-center gap-1">
          <TaskFeed className="rounded-lg" />
          <NotificationFeed className="rounded-lg" />
        </div>
      )}
    </div>
  )
}
